/**
 * Labels for the server's fixed choice codes (jewelry/models.py). The API sends
 * codes only; a code added there is a build error here until it has a label.
 */
import type { PickerOption } from "../components/Picker";
import type {
  Clarity,
  Condition,
  Currency,
  CutGrade,
  Metal,
  MetalColor,
  OwnershipStatus,
  PieceKind,
  StoneShape,
  StoneType,
} from "../api/types";
import type { FieldLabel } from "./fieldLabels";
import { pick } from "./labels";

type Labels<T extends string> = Record<T, FieldLabel>;

export const kindLabels: Labels<PieceKind> = {
  ring: { fa: "انگشتر", en: "Ring" },
  necklace: { fa: "گردنبند", en: "Necklace" },
  pendant: { fa: "آویز", en: "Pendant" },
  chain: { fa: "زنجیر", en: "Chain" },
  bracelet: { fa: "دستبند", en: "Bracelet" },
  bangle: { fa: "النگو", en: "Bangle" },
  earrings: { fa: "گوشواره", en: "Earrings" },
  brooch: { fa: "سنجاق سینه", en: "Brooch" },
  anklet: { fa: "پابند", en: "Anklet" },
  set: { fa: "سرویس", en: "Set" },
  watch: { fa: "ساعت", en: "Watch" },
  coin: { fa: "سکه", en: "Coin" },
  bar: { fa: "شمش", en: "Bar / bullion" },
  loose_stone: { fa: "سنگ بدون پایه", en: "Loose stone" },
  other: { fa: "سایر", en: "Other" },
};

export const metalLabels: Labels<Metal> = {
  gold: { fa: "طلا", en: "Gold" },
  silver: { fa: "نقره", en: "Silver" },
  platinum: { fa: "پلاتین", en: "Platinum" },
  palladium: { fa: "پالادیوم", en: "Palladium" },
  mixed: { fa: "چند فلزی", en: "Mixed metals" },
  other: { fa: "سایر / غیرقیمتی", en: "Other / non-precious" },
};

export const metalColorLabels: Labels<MetalColor> = {
  yellow: { fa: "زرد", en: "Yellow" },
  white: { fa: "سفید", en: "White" },
  rose: { fa: "رزگلد", en: "Rose" },
  two_tone: { fa: "دو رنگ", en: "Two-tone" },
  other: { fa: "سایر", en: "Other" },
};

export const ownershipLabels: Labels<OwnershipStatus> = {
  in_stock: { fa: "موجود", en: "In stock" },
  reserved: { fa: "رزرو", en: "Reserved" },
  sold: { fa: "فروخته‌شده", en: "Sold" },
  consigned: { fa: "امانی", en: "On consignment" },
  personal: { fa: "مجموعهٔ شخصی", en: "Personal collection" },
  repair: { fa: "در تعمیر", en: "Out for repair" },
};

export const conditionLabels: Labels<Condition> = {
  new: { fa: "نو", en: "New" },
  excellent: { fa: "عالی", en: "Excellent" },
  good: { fa: "خوب", en: "Good" },
  fair: { fa: "متوسط", en: "Fair" },
  needs_repair: { fa: "نیازمند تعمیر", en: "Needs repair" },
};

export const currencyLabels: Labels<Currency> = {
  IRT: { fa: "تومان", en: "Toman" },
  IRR: { fa: "ریال", en: "Rial" },
  USD: { fa: "دلار آمریکا", en: "US dollar" },
  EUR: { fa: "یورو", en: "Euro" },
  AED: { fa: "درهم امارات", en: "UAE dirham" },
};

export const stoneTypeLabels: Labels<StoneType> = {
  diamond: { fa: "الماس", en: "Diamond" },
  ruby: { fa: "یاقوت سرخ", en: "Ruby" },
  sapphire: { fa: "یاقوت کبود", en: "Sapphire" },
  emerald: { fa: "زمرد", en: "Emerald" },
  pearl: { fa: "مروارید", en: "Pearl" },
  turquoise: { fa: "فیروزه", en: "Turquoise" },
  agate: { fa: "عقیق", en: "Agate" },
  amethyst: { fa: "آمتیست", en: "Amethyst" },
  topaz: { fa: "توپاز", en: "Topaz" },
  garnet: { fa: "گارنت", en: "Garnet" },
  opal: { fa: "اپال", en: "Opal" },
  cubic_zirconia: { fa: "زیرکن", en: "Cubic zirconia" },
  other: { fa: "سایر", en: "Other" },
};

export const stoneShapeLabels: Labels<StoneShape> = {
  round: { fa: "گرد", en: "Round" },
  princess: { fa: "پرنسس", en: "Princess" },
  oval: { fa: "بیضی", en: "Oval" },
  cushion: { fa: "کوشن", en: "Cushion" },
  emerald: { fa: "زمردی", en: "Emerald cut" },
  pear: { fa: "اشکی", en: "Pear" },
  marquise: { fa: "مارکیز", en: "Marquise" },
  heart: { fa: "قلب", en: "Heart" },
  radiant: { fa: "رادیانت", en: "Radiant" },
  asscher: { fa: "آشر", en: "Asscher" },
  baguette: { fa: "باگت", en: "Baguette" },
  cabochon: { fa: "کابوشن", en: "Cabochon" },
  other: { fa: "سایر", en: "Other" },
};

export const cutLabels: Labels<CutGrade> = {
  excellent: { fa: "عالی", en: "Excellent" },
  very_good: { fa: "خیلی خوب", en: "Very good" },
  good: { fa: "خوب", en: "Good" },
  fair: { fa: "متوسط", en: "Fair" },
  poor: { fa: "ضعیف", en: "Poor" },
};

const CLARITY: Clarity[] = [
  "FL",
  "IF",
  "VVS1",
  "VVS2",
  "VS1",
  "VS2",
  "SI1",
  "SI2",
  "I1",
  "I2",
  "I3",
];
export const clarityLabels = Object.fromEntries(
  CLARITY.map((code) => [code, { fa: code, en: code }]),
) as Labels<Clarity>;

/** In declaration order, which is the order the trade reads them in. */
export function optionsOf<T extends string>(labels: Labels<T>): PickerOption[] {
  return (Object.entries(labels) as Array<[T, FieldLabel]>).map(([id, label]) => ({
    id,
    ...label,
  }));
}

/** Empty string (not recorded) renders as nothing, not as "undefined". */
export function labelOf<T extends string>(labels: Labels<T>, code: T | "" | null | undefined) {
  return code ? pick(labels[code]) : "";
}

/** Common hallmark purities per metal, as `[permille, karat-or-null]`. */
export const PURITY_PRESETS: Partial<Record<Metal, Array<[number, number | null]>>> = {
  gold: [
    [750, 18],
    [999, 24],
    [916, 22],
    [875, 21],
    [585, 14],
    [417, 10],
  ],
  silver: [
    [925, null],
    [999, null],
    [900, null],
    [800, null],
  ],
  platinum: [
    [950, null],
    [900, null],
  ],
  palladium: [
    [950, null],
    [500, null],
  ],
};

/** 750 → "18k · 750"; a purity with no karat name is just its number. */
export function purityLabel(permille: number | null | undefined, metal?: Metal | ""): string {
  if (permille === null || permille === undefined) return "";
  if (metal !== "gold") return String(permille);
  const karat = Math.round((permille / 1000) * 24 * 10) / 10;
  return `${karat}k · ${permille}`;
}
